import {
  Box, Card, CardContent, Typography, Chip, Grid, Table, TableBody,
  TableCell, TableHead, TableRow, Tabs, Tab, CircularProgress, Button,
  Divider, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem,
} from '@mui/material';
import {
  ArrowBack, LocalShipping, ShoppingCart, AccountBalance, Phone, Email,
  LocationOn, Payment, Business,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK') : '—';

function PayDialog({ open, supplier, onClose, onPaid }) {
  const dispatch = useDispatch();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handlePay = async () => {
    if (!amount || +amount <= 0) return;
    setSaving(true);
    try {
      await api.post(`/suppliers/${supplier.id}/payment`, { amount: +amount, method, description: note });
      dispatch(showSnackbar({ message: `Payment of ₨${fmt(+amount)} recorded`, severity: 'success' }));
      onPaid(); onClose();
      setAmount(''); setNote('');
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Pay Supplier — {supplier?.name}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <Typography color="text.secondary">
          Outstanding: <strong style={{ color: '#ef4444' }}>₨ {fmt(supplier?.outstanding_balance)}</strong>
        </Typography>
        <TextField label="Amount (₨) *" type="number" value={amount} onChange={e => setAmount(e.target.value)} fullWidth />
        <TextField label="Payment Method" select value={method} onChange={e => setMethod(e.target.value)} fullWidth>
          {['cash', 'bank_transfer', 'cheque', 'card'].map(m => (
            <MenuItem key={m} value={m} sx={{ textTransform: 'capitalize' }}>{m.replace('_', ' ')}</MenuItem>
          ))}
        </TextField>
        <TextField label="Note / Reference" value={note} onChange={e => setNote(e.target.value)} fullWidth />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handlePay} disabled={saving || !amount}>
          {saving ? 'Saving…' : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SupplierDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [payOpen, setPayOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/suppliers/${id}`);
      setData(res.data);
    } catch {
      dispatch(showSnackbar({ message: 'Supplier not found', severity: 'error' }));
      navigate('/suppliers');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data) return null;

  const purchases = data.purchases || [];
  const payments = data.payments || [];
  const totalPurchased = purchases.reduce((s, p) => s + +p.total_amount, 0);
  const totalPaid = purchases.reduce((s, p) => s + +p.paid_amount, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/suppliers')} variant="text">Back</Button>
        <Typography variant="h5" fontWeight={700}>{data.name}</Typography>
        {data.company && <Chip label={data.company} variant="outlined" icon={<Business />} />}
        <Chip
          label={data.outstanding_balance > 0 ? `Due: ₨${fmt(data.outstanding_balance)}` : 'Settled'}
          color={data.outstanding_balance > 0 ? 'error' : 'success'} size="small"
        />
        {data.outstanding_balance > 0 && (
          <Button variant="contained" color="warning" startIcon={<Payment />} size="small" onClick={() => setPayOpen(true)}>
            Make Payment
          </Button>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Profile card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'warning.main', fontSize: 22 }}>
                  {data.name?.[0]?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{data.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Since {fmtDate(data.created_at)}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {data.phone && <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Phone fontSize="small" color="action" />
                <Typography variant="body2">{data.phone}</Typography>
              </Box>}
              {data.email && <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Email fontSize="small" color="action" />
                <Typography variant="body2">{data.email}</Typography>
              </Box>}
              {(data.city || data.address) && <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                <LocationOn fontSize="small" color="action" sx={{ mt: 0.2 }} />
                <Typography variant="body2">{[data.address, data.city].filter(Boolean).join(', ')}</Typography>
              </Box>}
            </CardContent>
          </Card>
        </Grid>

        {/* KPI cards */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            {[
              { label: 'Total Purchases', value: `₨ ${fmt(totalPurchased)}`, color: 'primary.main' },
              { label: 'Total Orders', value: purchases.length, color: 'info.main' },
              { label: 'Total Paid', value: `₨ ${fmt(totalPaid)}`, color: 'success.main' },
              { label: 'Outstanding', value: `₨ ${fmt(data.outstanding_balance)}`, color: data.outstanding_balance > 0 ? 'error.main' : 'success.main' },
              { label: 'Payments Made', value: payments.length, color: 'warning.main' },
            ].map((kpi, i) => (
              <Grid item xs={6} sm={4} key={i}>
                <Card>
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                    <Typography variant="h6" fontWeight={700} color={kpi.color}>{kpi.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label={`Purchase History (${purchases.length})`} icon={<ShoppingCart />} iconPosition="start" />
          <Tab label={`Payment Ledger (${payments.length})`} icon={<AccountBalance />} iconPosition="start" />
        </Tabs>
        <CardContent sx={{ p: 0 }}>
          {tab === 0 && (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Due</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchases.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No purchases yet</TableCell></TableRow>
                )}
                {purchases.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell>{fmtDate(p.created_at)}</TableCell>
                    <TableCell align="right"><Typography fontWeight={700}>₨ {fmt(p.total_amount)}</Typography></TableCell>
                    <TableCell align="right"><Typography color="success.main">₨ {fmt(p.paid_amount)}</Typography></TableCell>
                    <TableCell align="right">
                      {p.due_amount > 0
                        ? <Typography color="error.main" fontWeight={700}>₨ {fmt(p.due_amount)}</Typography>
                        : <Typography color="success.main">—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={p.payment_status} size="small"
                        color={p.payment_status === 'paid' ? 'success' : p.payment_status === 'partial' ? 'warning' : 'error'}
                        sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {tab === 1 && (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount Paid</TableCell>
                  <TableCell align="right">Balance After</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>No payments recorded</TableCell></TableRow>
                )}
                {payments.map(tx => (
                  <TableRow key={tx.id} hover>
                    <TableCell>{fmtDate(tx.entry_date || tx.created_at)}</TableCell>
                    <TableCell>{tx.description}</TableCell>
                    <TableCell align="right"><Typography color="success.main" fontWeight={700}>₨ {fmt(tx.amount)}</Typography></TableCell>
                    <TableCell align="right"><Typography fontWeight={600}>₨ {fmt(tx.balance_after)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PayDialog open={payOpen} supplier={data} onClose={() => setPayOpen(false)} onPaid={load} />
    </Box>
  );
}
